import { apiFetch, ApiClientError } from './client.js';
import type { Catalog, ProjectCatalog } from '@supernova/shared';

export type { Catalog, ProjectCatalog };

export async function listCatalogs(): Promise<Catalog[]> {
  const data = await apiFetch<{ catalogs: Catalog[] }>('/api/v1/catalogs');
  return data.catalogs;
}

/**
 * Países que el cliente eligió en el portal (eko27): el selector muestra los eCostos y
 * catálogos públicos de esos países. null = mostrar todos (no eligió o eko27 no respondió).
 */
export async function getPaisesVisibles(): Promise<string[] | null> {
  try {
    const data = await apiFetch<{ paises: string[] | null }>('/api/v1/catalogs/paises-visibles');
    return Array.isArray(data.paises) && data.paises.length ? data.paises : null;
  } catch {
    return null;
  }
}

export async function getCatalog(id: string): Promise<Catalog> {
  const data = await apiFetch<{ catalog: Catalog }>(`/api/v1/catalogs/${id}`);
  return data.catalog;
}

export async function uploadCatalog(opts: {
  file: File;
  name: string;
  description?: string;
}): Promise<Catalog> {
  const form = new FormData();
  form.append('file', opts.file, opts.file.name);
  form.append('name', opts.name);
  if (opts.description) form.append('description', opts.description);

  const res = await fetch('/api/v1/catalogs', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiClientError(res.status, body);
  return (body as { catalog: Catalog }).catalog;
}

export async function getProjectCatalogs(projectId: string): Promise<ProjectCatalog[]> {
  const data = await apiFetch<{ projectCatalogs: ProjectCatalog[] }>(`/api/v1/projects/${projectId}/catalogs`);
  return data.projectCatalogs;
}

export async function setProjectCatalogs(projectId: string, catalogIds: string[]): Promise<ProjectCatalog[]> {
  const data = await apiFetch<{ projectCatalogs: ProjectCatalog[] }>(`/api/v1/projects/${projectId}/catalogs`, {
    method: 'PUT',
    body: { catalogIds },
  });
  return data.projectCatalogs;
}

export async function deleteCatalog(id: string): Promise<void> {
  await apiFetch(`/api/v1/catalogs/${id}`, { method: 'DELETE', acceptStatus: [204] as number[] });
}

export interface CatalogResource {
  id: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
  tipo: number;
  esCompuesto: boolean;
  precio: number | null;
  familia: string | null;
}

export async function listCatalogResources(
  catalogId: string,
  opts: {
    q?: string;
    limit?: number;
    offset?: number;
    problema?: 'sin_unidad' | 'sin_precio';
    /** Filtra por tipo(s) de recurso (ej. `[7]` = solo conceptos, `[1]` = solo materiales). */
    tipos?: number[];
    /** Solo los que tienen todas las palabras: cada palabra que se agrega afina. */
    todasLasPalabras?: boolean;
  } = {},
): Promise<{ total: number; resources: CatalogResource[] }> {
  const params = new URLSearchParams();
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  if (opts.problema) params.set('problema', opts.problema);
  if (opts.tipos && opts.tipos.length > 0) params.set('tipo', opts.tipos.join(','));
  if (opts.todasLasPalabras) params.set('todas', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/api/v1/catalogs/${catalogId}/resources${qs}`);
}

export interface CatalogComponent {
  parentClave: string;
  tipoComponente: number | null;
  childClave: string;
  childDescripcion: string | null;
  childUnidad: string | null;
  childTipo: number | null;
  cantidad: number | null;
  costoReferencia: number | null;
  indice: number | null;
}

export async function listCatalogComponents(
  catalogId: string,
  opts: { parent?: string; limit?: number; offset?: number } = {},
): Promise<{ total: number; components: CatalogComponent[] }> {
  const params = new URLSearchParams();
  if (opts.parent) params.set('parent', opts.parent);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/api/v1/catalogs/${catalogId}/components${qs}`);
}
